import { useState } from 'react';
import { Mail, Building2, Clock, CalendarDays, KeyRound, Loader2, Check, Sparkles, DoorOpen } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { PendingInvitation } from '@/types';

const DAY_NAMES = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

interface InvitationCardProps {
  invitation: PendingInvitation;
  onAccept: (id: string) => void;
  onDecline?: (id: string) => void;
  isAccepting: boolean;
  isDeclining?: boolean;
}

export function InvitationCard({ invitation, onAccept, onDecline, isAccepting, isDeclining }: InvitationCardProps) {
  const [accepted, setAccepted] = useState(false);
  const device = invitation.device;
  const org = invitation.organization;
  const isFacility = invitation.target_type === 'facility';
  const title = isFacility
    ? (invitation.facility?.name || 'Anläggning')
    : (device?.name || 'Enhet');

  const hasTimeRestriction = invitation.access_start_time || invitation.access_end_time;
  const hasDayRestriction = invitation.access_days.length < 7;
  const dayLabels = invitation.access_days.map((d) => DAY_NAMES[d - 1]).join(', ');
  const busy = isAccepting || isDeclining;

  // Anläggning: visa dörrarna som ingår — urvalet, alternativt alla dörrar.
  const allDoors = invitation.all_doors !== false;
  const doorLabel = isFacility
    ? allDoors
      ? `Alla dörrar${invitation.doors?.length ? ` (${invitation.doors.length})` : ''}`
      : (invitation.doors ?? []).map((d) => d.name).join(', ')
    : null;

  const handleAccept = () => {
    setAccepted(true);
    onAccept(invitation.id);
  };

  return (
    <div
      className={`transition-all ease-out ${
        accepted
          ? 'duration-300 scale-90 opacity-0 -translate-y-2 max-h-0'
          : 'duration-300 scale-100 opacity-100 translate-y-0 max-h-[600px]'
      }`}
    >
      <Card className="relative overflow-hidden">
        {/* Success overlay — uses primary (inverted fg/bg) for Quartr CTA feel */}
        {accepted && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-primary/95 backdrop-blur-md animate-fade-in rounded-2xl">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-foreground/20 ring-2 ring-primary-foreground/40">
              <Check className="h-7 w-7 text-primary-foreground" strokeWidth={3} />
            </div>
            <p className="mt-3 text-sm font-semibold text-primary-foreground">
              {isFacility ? 'Nycklar skapade' : 'Nyckel skapad'}
            </p>
          </div>
        )}

        {/* Animated shimmer sweep — neutral shine that works in both modes */}
        <div
          className="absolute inset-0 pointer-events-none animate-shimmer"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, hsl(var(--glass-shine)) 50%, transparent 100%)',
          }}
        />

        <CardContent className="relative p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--foreground)/0.08)] ring-1 ring-[hsl(var(--glass-stroke))]">
                {isFacility ? (
                  <Building2 className="h-5 w-5 text-foreground" />
                ) : (
                  <Mail className="h-5 w-5 text-foreground" />
                )}
              </div>
              <div>
                <p className="font-semibold text-base">{title}</p>
                {isFacility ? (
                  <p className="text-sm text-muted-foreground">
                    {allDoors ? 'Hela anläggningen' : 'Utvalda dörrar'}
                    {org?.name ? ` · ${org.name}` : ''}
                  </p>
                ) : (
                  org?.name && <p className="text-sm text-muted-foreground">{org.name}</p>
                )}
              </div>
            </div>
            <Badge
              variant="outline"
              className="border-[hsl(var(--glass-stroke))] text-foreground bg-[hsl(var(--foreground)/0.06)] text-xs gap-1 backdrop-blur-md"
            >
              <Sparkles className="h-3 w-3" />
              Inbjudan
            </Badge>
          </div>

          {/* Details */}
          <div className="space-y-2 mb-4">
            {doorLabel && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <DoorOpen className="h-3.5 w-3.5 shrink-0" />
                <span>{doorLabel}</span>
              </div>
            )}
            {hasTimeRestriction && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                <span>{invitation.access_start_time?.slice(0, 5)} – {invitation.access_end_time?.slice(0, 5)}</span>
              </div>
            )}
            {hasDayRestriction && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                <span>{dayLabels}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <KeyRound className="h-3.5 w-3.5 shrink-0" />
              <span>
                {invitation.key_type === 'single_use' ? 'Engångsnyckel' :
                 invitation.key_type === 'multi_use' ? 'Flergångsnyckel' :
                 invitation.key_type === 'open_close' ? 'Öppna & stäng' :
                 invitation.key_type === 'open_only' ? 'Endast öppna' : 'Nyckel'}
                {' · '}{invitation.key_validity_hours}h giltig
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              className="flex-1"
              onClick={handleAccept}
              disabled={busy || accepted}
            >
              {isAccepting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Accepterar...
                </>
              ) : accepted ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Accepterad
                </>
              ) : (
                'Acceptera'
              )}
            </Button>

            {onDecline && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive px-3"
                onClick={() => onDecline(invitation.id)}
                disabled={busy}
              >
                {isDeclining ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Neka'
                )}
              </Button>
            )}
          </div>

          {device?.location && (
            <p className="text-xs text-muted-foreground text-center mt-2">{device.location}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
